// Communication log tab for Customer 360. Shows every invoice, OTP or
// campaign message sent to this customer across channels.

import type { ColumnDef } from '@bbpos/ui';
import { Badge } from '@bbpos/ui';
import { ReportTable } from '@/features/reports';
import type { CommunicationLogRow } from '../types';
import { formatDateTime } from '../format';

type Row = CommunicationLogRow;

const STATUS_VARIANT: Record<Row['status'], 'info' | 'success' | 'warning' | 'danger'> = {
  QUEUED: 'info',
  SENT: 'info',
  DELIVERED: 'success',
  OPENED: 'success',
  FAILED: 'danger',
};

const CHANNEL_VARIANT: Record<Row['channel'], 'info' | 'success' | 'warning'> = {
  EMAIL: 'info',
  WHATSAPP: 'success',
  SMS: 'warning',
};

const columns: ColumnDef<Row>[] = [
  {
    key: 'sent_at',
    header: 'Sent',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300 tabular-nums">{formatDateTime(row.sent_at)}</span>,
  },
  {
    key: 'channel',
    header: 'Channel',
    render: (_v, row) => <Badge variant={CHANNEL_VARIANT[row.channel]} size="sm">{row.channel}</Badge>,
  },
  {
    key: 'template',
    header: 'Template',
    render: (_v, row) => (
      <div>
        <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">{row.template}</p>
        {row.subject ? <p className="text-xs text-neutral-500 dark:text-neutral-400">{row.subject}</p> : null}
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => <Badge variant={STATUS_VARIANT[row.status]} size="sm">{row.status}</Badge>,
  },
  {
    key: 'reference',
    header: 'Reference',
    render: (_v, row) => <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{row.reference ?? '—'}</span>,
  },
];

export interface CommunicationLogTableProps {
  rows: Row[];
  loading?: boolean;
}

export function CommunicationLogTable({ rows, loading }: CommunicationLogTableProps) {
  return (
    <ReportTable<Row>
      title="Communication Log"
      subtitle="Invoices, campaigns and transactional messages."
      columns={columns}
      data={rows}
      loading={loading}
      totalCount={rows.length}
      emptyTitle="No messages sent"
      emptyDescription="Digital invoices, OTPs and campaign messages will appear here."
    />
  );
}
